import { useEffect, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  FieldViewBlock,
  RadioButton,
  REQUIRED_ERROR_MESSAGE,
  TextInput,
} from "@SeemplicityDev/shared";
import type { AutomationWizardStepProps } from "../../../AutomationsUtils";
import "./TicketReopenConfig.scss";

const REOPEN_DAYS_BASE_RULES = {
  required: REQUIRED_ERROR_MESSAGE,
  pattern: {
    value: /^[1-9][0-9]*$/,
    message: "Invalid number of days",
  },
  max: {
    value: 500,
    message: "Number of days cannot be greater than 500.",
  },
} as const;

// deps causes RHF to re-validate the sibling field whenever this one changes,
// so stale cross-field errors clear automatically without manual trigger() calls.
const DAYS_TO_REOPEN_RULES = {
  ...REOPEN_DAYS_BASE_RULES,
  deps: ["ticketsReopenStartThreshold"],
};

const TICKETS_REOPEN_START_THRESHOLD_RULES = {
  ...REOPEN_DAYS_BASE_RULES,
  deps: ["daysToReopenTickets"],
  validate: (
    value: number,
    { daysToReopenTickets }: Record<string, unknown>
  ) => {
    if (!daysToReopenTickets) {
      return true;
    } else if (Number(value) >= Number(daysToReopenTickets)) {
      return true;
    } else {
      return "Age limit must be greater than or equal to the reopen trigger period.";
    }
  },
};

// In "unlimited" mode (radio B) the age-limit field is disabled, so drop every
// rule — required / pattern / max / the cross-field validator all stop applying.
const NO_RULES = {};

type TicketReopenConfigProps = {
  isSummary: AutomationWizardStepProps["isSummary"];
};

const TicketReopenConfig: React.FC<TicketReopenConfigProps> = ({
  isSummary,
}) => {
  const {
    control,
    trigger,
    formState: { errors },
  } = useFormContext();

  const daysToReopenTickets = useWatch({
    control,
    name: "daysToReopenTickets",
  });
  const ticketsReopenStartThreshold = useWatch({
    control,
    name: "ticketsReopenStartThreshold",
  });
  const ticketsReopenUnlimited = useWatch({
    control,
    name: "ticketsReopenUnlimited",
  });

  // Re-validate the age-limit field whenever the reopen-window mode flips, so a
  // stale mode-(A) error clears when the user switches to "any ticket" (B) and
  // vice-versa (rules are read at validation time from the current registration).
  useEffect(() => {
    trigger("ticketsReopenStartThreshold");
  }, [ticketsReopenUnlimited, trigger]);

  const configSummary = useMemo(() => {
    if (!daysToReopenTickets) {
      return undefined;
    }
    if (ticketsReopenUnlimited) {
      return `Reopen after ${daysToReopenTickets} days, regardless of when the ticket was created.`;
    }
    if (ticketsReopenStartThreshold) {
      return `Reopen after ${daysToReopenTickets} days, starting ${ticketsReopenStartThreshold} days after ticket creation.`;
    }
    return undefined;
  }, [
    daysToReopenTickets,
    ticketsReopenStartThreshold,
    ticketsReopenUnlimited,
  ]);

  return isSummary ? (
    <FieldViewBlock label="Reopen Ticket" text={configSummary} />
  ) : (
    <div className="reopen-configuration">
      <div className="reopen-row">
        <span>
          Automatically reopen tickets, if the finding is not fixed after
        </span>
        <Controller
          name="daysToReopenTickets"
          render={({ field: { value, onChange, onBlur } }) => (
            <TextInput
              data-testid="days-to-reopen-tickets"
              onChange={onChange}
              name="daysToReopenTickets"
              onBlur={onBlur}
              value={value}
              error={errors?.daysToReopenTickets}
              min={1}
              type="number"
              placeholder="1-500"
              hideLabelHelper
              noMargin
            />
          )}
          control={control}
          rules={DAYS_TO_REOPEN_RULES}
        />
        <span>days</span>
      </div>
      <Controller
        name="ticketsReopenUnlimited"
        control={control}
        render={({ field: { value: isUnlimited, onChange } }) => (
          <div className="reopen-window-options">
            <div className="reopen-row">
              <RadioButton
                data-testid="reopen-window-limited"
                name="reopen-window-limited"
                value="limited"
                checked={!isUnlimited}
                onChange={() => onChange(false)}
                label="Only reopen tickets created within the last"
              />
              <Controller
                name="ticketsReopenStartThreshold"
                render={({
                  field: { value, onChange: onThreshold, onBlur },
                }) => (
                  <TextInput
                    data-testid="tickets-reopen-start-threshold"
                    onChange={onThreshold}
                    name="ticketsReopenStartThreshold"
                    onBlur={onBlur}
                    value={value}
                    error={errors?.ticketsReopenStartThreshold}
                    min={1}
                    type="number"
                    placeholder="1-500"
                    hideLabelHelper
                    noMargin
                    disabled={isUnlimited}
                  />
                )}
                control={control}
                rules={
                  isUnlimited ? NO_RULES : TICKETS_REOPEN_START_THRESHOLD_RULES
                }
              />
              <span>days</span>
            </div>
            <div className="reopen-row">
              <RadioButton
                data-testid="reopen-window-unlimited"
                name="reopen-window-unlimited"
                value="unlimited"
                checked={isUnlimited}
                onChange={() => onChange(true)}
                label="Reopen any ticket, regardless of its creation date"
              />
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default TicketReopenConfig;
